import { Suspense, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import DashboardLayout from "layouts/common/DashboardLayout";
import ContentLayout from "layouts/ContentLayout";
import DefaultHeader from "layouts/Header/Component/defaultHeader";
import HeaderLayout from "layouts/Header/HeaderLayout";
import Sidenav from "layouts/Sidenav";

import appRoutes from "./appRouter";
import defaultRoutes from "./defaultRouter";

import {
  checkPublicSelector,
  loginSelector,
  logoutStateSelector,
} from "modules/Auth/redux/selector";

import LoadingIcon from "components/common/LoadingIcon";
import ModalVerify from "modules/Auth/Pages/Login/ModalVerify";
import { ReduxStateType } from "redux-store/types";
import headerRoutes from "./headerRouter";
import pathRoutes from "./pathRoutes";
import { roleMenuDataSelector } from "layouts/Sidenav/redux/selector";
import { Typography } from "@mui/material";
import pxToRem from "theme/base/pxToRem";

function Layout() {
  const { pathname } = useLocation();

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  return (
    <>
      <ModalVerify />
      <Sidenav />
      <DashboardLayout>
        <Routes>
          <Route path="/" element={<HeaderLayout />}>
            {headerRoutes.map((route) => {
              const { page, path } = route;
              return <Route key={path} {...route} element={page} />;
            })}
            <Route path="*" element={<DefaultHeader />} />
          </Route>
        </Routes>
        <ContentLayout>
          <Suspense fallback={<LoadingIcon />}>
            {/* Outlet sẽ render các route con tại đây */}
            <Outlet />
          </Suspense>
        </ContentLayout>
      </DashboardLayout>
    </>
  );
}

function AppRouterProvider() {
  const navigate = useNavigate();
  const { status: statusAuth } = useSelector(loginSelector);
  const justLogout = useSelector(logoutStateSelector);
  const roles = useSelector(roleMenuDataSelector);
  const usePublicNetwork = useSelector(checkPublicSelector);
  const [preUrl, setPreUrl] = useState<string | null>(null);
  const isAuth = useMemo(() => {
    return statusAuth === ReduxStateType.SUCCESS;
  }, [statusAuth]);

  const location = useLocation();

  const NoPermission = () => {
    return (
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: pxToRem(20),
          width: "100%",
          textAlign: "center",
          marginTop: pxToRem(20),
        }}
      >
        Bạn không có quyền truy cập!
      </Typography>
    );
  };

  useEffect(() => {
    if (!isAuth) {
      if (!justLogout) {
        setPreUrl(location.pathname);
      }
      navigate(pathRoutes.auth.login);
    } else {
      let path = location.pathname;
      if (
        preUrl === pathRoutes.auth.login ||
        (location.pathname === pathRoutes.auth.login && preUrl == null)
      ) {
        path = usePublicNetwork
          ? `${pathRoutes.declareLeave.base}${pathRoutes.declareLeave.tabs.timeKeeping}`
          : pathRoutes.homepage;
      } else if (preUrl && preUrl !== pathRoutes.auth.login) {
        path = preUrl;
      }
      navigate(path);
      setPreUrl(null);
    }
  }, [
    isAuth,
    justLogout,
    location.pathname,
    navigate,
    preUrl,
    usePublicNetwork,
  ]);

  return (
    <Routes>
      {defaultRoutes.map((route) => {
        const { page, path } = route;
        const Element = page;
        return (
          <Route
            key={path}
            {...route}
            element={
              <Suspense fallback={<LoadingIcon />}>
                <Element />
              </Suspense>
            }
          />
        );
      })}
      <Route path="*" element={<Navigate to={pathRoutes.homepage} replace />} />

      {/* Route không có layout */}
      <Route path="/" element={<Layout />}>
        {appRoutes.map((route) => {
          const { page, path, children, screenCode: screen } = route;
          const Element = page;
          return (
            <Route
              key={path}
              path={path}
              element={
                !screen || roles.includes(screen) ? (
                  <Element />
                ) : (
                  <NoPermission />
                )
              }
            >
              {children?.map((child) => {
                const { page: pageChild, path: pathChild } = child;
                const ElementChild = pageChild;
                return (
                  <Route
                    key={pathChild}
                    path={pathChild}
                    element={
                      !screen || roles.includes(screen) ? (
                        <ElementChild />
                      ) : (
                        <NoPermission />
                      )
                    }
                  />
                );
              })}
            </Route>
          );
        })}
      </Route>
    </Routes>
  );
}

export default AppRouterProvider;
